import React, { useState, useMemo, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Lazy load ProductCard
const ProductCard = lazy(() => import("./ProductCard"));

const FeaturedProducts = React.memo(() => {
  const products = useMemo(
    () => [
      { image: "/img/tantalum for.jpg", title: "Tantalum(V) Chloride" },
      { image: "/img/silicon.png", title: "Silicon Powder" },
      { image: "/img/propionic anhydride.png", title: "Propionic Anhydride" },
      { image: "/img/download1.png", title: "Acetone" },
      { image: "/img/download2.png", title: "Sodium Hydroxide" },
      { image: "/img/download3.jpg", title: "Ethanol" },
    ],
    []
  );

  const itemsPerPage = 3;
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  const displayedProducts = useMemo(
    () =>
      products.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [products, currentPage, itemsPerPage]
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <section className="py-24 bg-[#f8f9fb]">
      {/* Heading */}
      <div className="text-center mb-16">
        <h2 className="text-2xl md:text-3xl font-light tracking-widest text-gray-600">
          FEATURED
        </h2>
        <h1 className="text-4xl md:text-5xl font-bold mt-2">
          OUR <span className="text-[#B80000]">PRODUCTS</span>
        </h1>
        <div className="mt-3 w-24 h-[3px] bg-[#B80000] mx-auto rounded-full"></div>
      </div>

      {/* Product Grid */}
      <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
        <AnimatePresence mode="wait">
          {displayedProducts.map((p) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
            >
              <Suspense
                fallback={
                  <div className="w-full h-72 bg-gray-100 animate-pulse rounded-xl" />
                }
              >
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500">
                  <div className="relative bg-white flex items-center justify-center overflow-hidden group">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-[250px] object-contain transition-transform duration-500 group-hover:scale-105 p-4"
                    />
                    <div className="absolute inset-0 border-2 border-[#B80000] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 hover:text-[#B80000] transition-colors duration-300">
                      {p.title}
                    </h3>
                  </div>
                </div>
              </Suspense>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-16 gap-3">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous Page"
          className={`w-10 h-10 rounded-full border flex items-center justify-center text-base font-medium transition-all duration-300 ${
            currentPage === 1
              ? "text-gray-400 border-gray-300 cursor-not-allowed"
              : "text-[#B80000] border-[#B80000] hover:bg-[#B80000] hover:text-white"
          }`}
        >
          &lt;
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            aria-label={`Go to page ${page}`}
            className={`w-10 h-10 rounded-full border flex items-center justify-center text-base font-medium transition-all duration-300 ${
              currentPage === page
                ? "bg-[#B80000] text-white border-[#B80000]"
                : "text-[#B80000] border-[#B80000] hover:bg-[#B80000] hover:text-white"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next Page"
          className={`w-10 h-10 rounded-full border flex items-center justify-center text-base font-medium transition-all duration-300 ${
            currentPage === totalPages
              ? "text-gray-400 border-gray-300 cursor-not-allowed"
              : "text-[#B80000] border-[#B80000] hover:bg-[#B80000] hover:text-white"
          }`}
        >
          &gt;
        </button>
      </div>
    </section>
  );
});

export default FeaturedProducts;
